import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Pencil, Image as ImageIcon, X, Eye, EyeOff } from 'lucide-react';
import { apiUrl, adminFetch } from '../../api';

const EMPTY = { title: '', category: '', excerpt: '', content: '', readTime: '', image: '', author: '', published: true };

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null); // null = creating new
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await adminFetch(apiUrl('/api/admin/blogs'));
      const data = await res.json();
      if (data.success) setBlogs(data.blogs);
    } catch {
      setError('Could not load blogs. Is the server running?');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditingId(null); setForm(EMPTY); setMsg(''); setError(''); };

  const startEdit = (b) => {
    setEditingId(b._id);
    setForm({
      title: b.title || '', category: b.category || '', excerpt: b.excerpt || '',
      content: b.content || '', readTime: b.readTime || '', image: b.image || '',
      author: b.author || '', published: b.published !== false,
    });
    setMsg(''); setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onPickImage = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, image: b64 }));
    e.target.value = '';
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setBusy(true); setError(''); setMsg('');
    try {
      const url = editingId ? `/api/admin/blogs/${editingId}` : '/api/admin/blogs';
      const res = await adminFetch(apiUrl(url), {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Save failed'); return; }
      setMsg(editingId ? 'Blog updated.' : 'Blog published.');
      setForm(EMPTY); setEditingId(null);
      await load();
    } catch {
      setError('Could not save. Is the server running?');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    await adminFetch(apiUrl(`/api/admin/blogs/${id}`), { method: 'DELETE' });
    if (editingId === id) startNew();
    await load();
  };

  return (
    <div className="admin-shell">
      <main className="admin-main">
        <header className="admin-main-header">
          <div>
            <h2>{editingId ? 'Edit Blog Post' : 'New Blog Post'}</h2>
            <p className="admin-muted">Write a journal article. It appears on the homepage journal and the /blog page.</p>
          </div>
          {editingId && (
            <button className="admin-btn-primary" onClick={startNew}><Plus size={16} /> New Post</button>
          )}
        </header>

        {error && <div className="admin-error">{error}</div>}
        {msg && <div className="admin-success">{msg}</div>}

        <form className="admin-listing-form" onSubmit={save}>
          <div className="admin-form-grid">
            <div className="admin-field span2">
              <label className="admin-label">Title *</label>
              <input className="admin-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Category / Tag</label>
              <input className="admin-input" placeholder="e.g. WELLNESS" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Read time</label>
              <input className="admin-input" placeholder="e.g. 4 min read" value={form.readTime}
                onChange={(e) => setForm({ ...form, readTime: e.target.value })} />
            </div>
            <div className="admin-field span2">
              <label className="admin-label">Excerpt (short summary)</label>
              <textarea className="admin-input" rows={2} value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </div>
            <div className="admin-field span2">
              <label className="admin-label">Full content</label>
              <textarea className="admin-input" rows={10} placeholder="Write the full article. Leave a blank line between paragraphs."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Author</label>
              <input className="admin-input" placeholder="Elaichi & Love" value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Published</label>
              <label className="admin-toggle-row" style={{ marginTop: 6 }}>
                <input type="checkbox" checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                <span className="admin-muted sm">{form.published ? 'Visible to customers' : 'Hidden (draft)'}</span>
              </label>
            </div>
          </div>

          <div className="admin-field">
            <label className="admin-label">Cover image (optional)</label>
            <div className="admin-image-row">
              {form.image && (
                <div className="admin-thumb">
                  <img src={form.image} alt="cover" />
                  <button type="button" className="admin-thumb-x" onClick={() => setForm({ ...form, image: '' })}><X size={12} /></button>
                </div>
              )}
              {!form.image && (
                <label className="admin-upload-box">
                  <ImageIcon size={18} />
                  <span>Add</span>
                  <input type="file" accept="image/*" hidden onChange={onPickImage} />
                </label>
              )}
            </div>
          </div>

          <button className="admin-btn-primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : (editingId ? 'Update Post' : 'Publish Post')}
          </button>
        </form>

        <header className="admin-main-header" style={{ marginTop: 32 }}>
          <div><h2>All Posts ({blogs.length})</h2></div>
        </header>

        <div className="admin-listing-grid">
          {blogs.length === 0 && <p className="admin-muted">No blog posts yet. Create one above.</p>}
          {blogs.map((b) => (
            <div className="admin-listing-card" key={b._id}>
              <div className="admin-listing-thumb">
                {b.image
                  ? <img src={b.image} alt={b.title} />
                  : <div className="admin-listing-noimg"><ImageIcon size={22} /></div>}
              </div>
              <div className="admin-listing-body">
                <h4>{b.title}</h4>
                <p className="admin-muted sm">
                  {[b.category, b.readTime].filter(Boolean).join(' · ')}
                  {b.published ? <span> · <Eye size={12} /> Live</span> : <span> · <EyeOff size={12} /> Draft</span>}
                </p>
                {b.excerpt && <p className="admin-listing-desc">{b.excerpt}</p>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button className="admin-icon-btn" onClick={() => startEdit(b)} title="Edit"><Pencil size={16} /></button>
                <button className="admin-icon-btn danger" onClick={() => remove(b._id)} title="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default AdminBlogs;
